import { Fragment, useMemo, useState } from "react";
import { Box, Card, FormControl, InputLabel, MenuItem, Select, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ProjectListItem } from "../../types/projectTypes";
import { conditionColor, formatDate, formatMoney, formatPercent, panelSx, splitList } from "./projectUtils";

type ProjectTimelineProps = {
    items: ProjectListItem[];
    onDetailsClick: (id: number) => void;
};

type Range = '2y' | '5y' | 'all';
type GroupBy = 'none' | 'director' | 'client';

const LABEL_WIDTH = 300;
const ROW_HEIGHT = 34;
const BAR_HEIGHT = 14;
const DAY = 24 * 60 * 60 * 1000;

const parse = (value: string | null | undefined): number | null => {
    if (!value) return null;
    const time = new Date(String(value).slice(0, 10)).getTime();
    return Number.isNaN(time) ? null : time;
};

const addYears = (time: number, years: number) => {
    const d = new Date(time);
    d.setFullYear(d.getFullYear() + years);
    return d.getTime();
};

type TimelineRow = { item: ProjectListItem; start: number | null; end: number };

// Gantt-style overview of the (filtered) projects: one row per project from its start (or the
// first invoice when no start date is recorded) to its end date. The darker part of each bar is
// the share already invoiced; the vertical line is today.
function ProjectTimeline({ items, onDetailsClick }: ProjectTimelineProps) {
    const theme = useTheme();
    const [range, setRange] = useState<Range>('5y');
    const [groupBy, setGroupBy] = useState<GroupBy>('none');
    const today = useMemo(() => new Date(new Date().toDateString()).getTime(), []);

    const rows: TimelineRow[] = useMemo(() => items
        .map((item) => ({ item, start: parse(item.effective_start_date), end: parse(item.project_end_date) }))
        .filter((row): row is TimelineRow => row.end !== null)
        .sort((a, b) => (a.start ?? a.end) - (b.start ?? b.end) || a.end - b.end), [items]);
    const withoutDates = items.length - rows.length;

    const [min, max] = useMemo(() => {
        if (range === '2y') return [addYears(today, -1), addYears(today, 1)];
        if (range === '5y') return [addYears(today, -2), addYears(today, 3)];
        const starts = rows.map((r) => r.start ?? r.end);
        const ends = rows.map((r) => r.end);
        if (!rows.length) return [addYears(today, -1), addYears(today, 1)];
        return [Math.min(today, ...starts) - 30 * DAY, Math.max(today, ...ends) + 30 * DAY];
    }, [range, rows, today]);

    const span = max - min;
    const pos = (time: number) => Math.min(100, Math.max(0, ((time - min) / span) * 100));

    // Year labels with quarter ticks
    const ticks = useMemo(() => {
        const result: { time: number; label: string; major: boolean }[] = [];
        const first = new Date(min);
        const cursor = new Date(first.getFullYear(), Math.floor(first.getMonth() / 3) * 3, 1);
        while (cursor.getTime() <= max) {
            if (cursor.getTime() >= min) {
                const major = cursor.getMonth() === 0;
                result.push({ time: cursor.getTime(), label: major ? String(cursor.getFullYear()) : `Q${cursor.getMonth() / 3 + 1}`, major });
            }
            cursor.setMonth(cursor.getMonth() + 3);
        }
        return result;
    }, [min, max]);
    const showQuarterLabels = span < 3.5 * 365 * DAY;

    const groups = useMemo(() => {
        if (groupBy === 'none') return [{ key: '', rows }];
        const map = new Map<string, TimelineRow[]>();
        rows.forEach((row) => {
            const keys = groupBy === 'director' ? splitList(row.item.project_directors) : [row.item.client || ''];
            (keys.length ? keys : ['']).forEach((key) => map.set(key || 'Not assigned', [...(map.get(key || 'Not assigned') || []), row]));
        });
        return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([key, groupRows]) => ({ key, rows: groupRows }));
    }, [rows, groupBy]);

    const barColor = theme.palette.primary.main;
    const gridColor = alpha(theme.palette.text.primary, 0.08);

    const renderBar = ({ item, start, end }: TimelineRow) => {
        const visibleStart = start ?? end;
        if (end < min || visibleStart > max) {
            return (
                <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', [end < min ? 'left' : 'right']: 4, top: 8 }}>
                    {end < min ? `← ended ${formatDate(item.project_end_date)}` : `starts ${formatDate(item.effective_start_date)} →`}
                </Typography>
            );
        }
        const left = pos(visibleStart);
        const width = Math.max(pos(end) - left, 0.6);
        const invoiced = Math.min(1, Math.max(0, Number(item.completion_ratio ?? 0)));
        const notStarted = item.status === 'Yet to start';
        const escalation = parse(item.price_escalation_date);

        const tooltip = (
            <Box sx={{ padding: 0.5, maxWidth: 320 }}>
                <Typography variant="subtitle2">{item.project_number} - {item.project_name}</Typography>
                <Typography variant="body2">{item.client || '-'} · {item.countries || '-'} · {item.lead ? 'INT lead' : 'INT partner'}</Typography>
                <Typography variant="body2">
                    {start ? formatDate(item.effective_start_date) : 'no start date'}{item.start_is_estimated ? ' (estimated)' : ''} – {formatDate(item.project_end_date)}
                </Typography>
                <Typography variant="body2">Invoiced {formatPercent(item.completion_ratio)} · time {formatPercent(item.time_elapsed_ratio)}</Typography>
                <Typography variant="body2">Open INT volume {formatMoney(item.int_open_volume, item.currency)}</Typography>
                <Typography variant="body2">{item.status || 'No status'}{item.project_condition ? ` · ${item.project_condition}` : ''}</Typography>
            </Box>
        );

        return (
            <>
                <Tooltip title={tooltip} arrow followCursor>
                    <Box
                        onClick={() => onDetailsClick(item.ID)}
                        sx={{
                            position: 'absolute', top: (ROW_HEIGHT - BAR_HEIGHT) / 2, height: BAR_HEIGHT, left: `${left}%`, width: `${width}%`,
                            borderRadius: '4px', cursor: 'pointer', overflow: 'hidden',
                            backgroundColor: notStarted ? 'transparent' : alpha(barColor, 0.22),
                            border: notStarted ? `1.5px dashed ${barColor}` : 'none',
                            // estimated start: fade in the left edge
                            ...(item.start_is_estimated && !notStarted ? {
                                maskImage: 'linear-gradient(90deg, rgba(0,0,0,0.25), #000 18px)',
                                WebkitMaskImage: 'linear-gradient(90deg, rgba(0,0,0,0.25), #000 18px)',
                            } : {}),
                            transition: 'box-shadow 0.15s ease',
                            '&:hover': { boxShadow: `0 0 0 2px ${alpha(barColor, 0.4)}` },
                        }}
                    >
                        {!notStarted && start !== null && (
                            <Box sx={{ height: '100%', width: `${invoiced * 100}%`, backgroundColor: barColor }} />
                        )}
                    </Box>
                </Tooltip>
                {escalation !== null && escalation >= min && escalation <= max && (
                    <Tooltip title={`Price escalation ${formatDate(item.price_escalation_date)}`} arrow>
                        <Box sx={{
                            position: 'absolute', top: ROW_HEIGHT / 2 - 5, left: `calc(${pos(escalation)}% - 5px)`, width: 10, height: 10,
                            transform: 'rotate(45deg)', backgroundColor: theme.palette.warning.main,
                            boxShadow: `0 0 0 2px ${theme.palette.background.paper}`, borderRadius: '2px',
                        }} />
                    </Tooltip>
                )}
            </>
        );
    };

    const gridLines = (
        <>
            {ticks.map((tick) => (
                <Box key={tick.time} sx={{
                    position: 'absolute', top: 0, bottom: 0, left: `${pos(tick.time)}%`, width: '1px',
                    backgroundColor: tick.major ? alpha(theme.palette.text.primary, 0.14) : gridColor,
                }} />
            ))}
            {today >= min && today <= max && (
                <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `calc(${pos(today)}% - 1px)`, width: '2px', backgroundColor: theme.palette.error.main, opacity: 0.8 }} />
            )}
        </>
    );

    return (
        <Card elevation={0} sx={{ ...panelSx(theme), padding: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} gap={1.5} sx={{ marginBottom: 2 }}>
                <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                    <ToggleButtonGroup size="small" exclusive value={range} onChange={(_, value) => value && setRange(value)}>
                        <ToggleButton value="2y">±1 year</ToggleButton>
                        <ToggleButton value="5y">5 years</ToggleButton>
                        <ToggleButton value="all">Fit all</ToggleButton>
                    </ToggleButtonGroup>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Group by</InputLabel>
                        <Select label="Group by" value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)}>
                            <MenuItem value="none">No grouping</MenuItem>
                            <MenuItem value="director">Project Director</MenuItem>
                            <MenuItem value="client">Client</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
                {/* Legend */}
                <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                    <Stack direction="row" gap={0.75} alignItems="center">
                        <Box sx={{ width: 28, height: 10, borderRadius: '3px', overflow: 'hidden', display: 'flex', backgroundColor: alpha(barColor, 0.22) }}>
                            <Box sx={{ width: '55%', backgroundColor: barColor }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">invoiced / remaining</Typography>
                    </Stack>
                    <Stack direction="row" gap={0.75} alignItems="center">
                        <Box sx={{ width: 28, height: 10, borderRadius: '3px', border: `1.5px dashed ${barColor}` }} />
                        <Typography variant="caption" color="text.secondary">yet to start</Typography>
                    </Stack>
                    <Stack direction="row" gap={0.75} alignItems="center">
                        <Box sx={{ width: 10, height: 10, transform: 'rotate(45deg)', backgroundColor: theme.palette.warning.main, borderRadius: '2px' }} />
                        <Typography variant="caption" color="text.secondary">price escalation</Typography>
                    </Stack>
                    <Stack direction="row" gap={0.75} alignItems="center">
                        <Box sx={{ width: 2, height: 14, backgroundColor: theme.palette.error.main }} />
                        <Typography variant="caption" color="text.secondary">today</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">faded start = estimated from first invoice</Typography>
                </Stack>
            </Stack>

            {rows.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" paddingY={6}>No projects with an end date match these filters.</Typography>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 800 }}>
                        {/* Axis */}
                        <Box sx={{ display: 'flex', position: 'sticky', top: 64, zIndex: 2, backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
                            <Box sx={{ width: LABEL_WIDTH, flexShrink: 0 }} />
                            <Box sx={{ position: 'relative', flexGrow: 1, height: 26 }}>
                                {ticks.filter((t) => t.major || showQuarterLabels).map((tick) => (
                                    <Typography key={tick.time} variant="caption" sx={{
                                        position: 'absolute', left: `${pos(tick.time)}%`, top: 4, paddingLeft: 0.5, whiteSpace: 'nowrap',
                                        color: tick.major ? 'text.primary' : 'text.secondary', fontWeight: tick.major ? 600 : 400,
                                    }}>
                                        {tick.label}
                                    </Typography>
                                ))}
                                {today >= min && today <= max && (
                                    <Typography variant="caption" sx={{ position: 'absolute', left: `${pos(today)}%`, top: 4, transform: 'translateX(-50%)', color: 'error.main', fontWeight: 600, backgroundColor: 'background.paper', paddingX: 0.5 }}>
                                        Today
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {groups.map((group) => (
                            <Fragment key={group.key || 'all'}>
                                {group.key && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', height: 30, backgroundColor: 'action.hover', paddingX: 1, marginTop: 1 }}>
                                        <Typography variant="subtitle2">{group.key}</Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ marginLeft: 1 }}>{group.rows.length} project(s)</Typography>
                                    </Box>
                                )}
                                {group.rows.map((row) => (
                                    <Box key={`${group.key}-${row.item.ID}`} sx={{ display: 'flex', height: ROW_HEIGHT, borderBottom: 1, borderColor: gridColor, '&:hover': { backgroundColor: 'action.hover' } }}>
                                        <Box onClick={() => onDetailsClick(row.item.ID)}
                                            sx={{ width: LABEL_WIDTH, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1, paddingX: 1, cursor: 'pointer', minWidth: 0 }}>
                                            <Tooltip title={`Condition: ${row.item.project_condition || 'not set'}`}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: conditionColor(row.item.project_condition) }} />
                                            </Tooltip>
                                            <Typography variant="body2" noWrap title={row.item.project_name || ''}>
                                                <b>{row.item.project_number}</b> {row.item.project_name}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ position: 'relative', flexGrow: 1 }}>
                                            {gridLines}
                                            {renderBar(row)}
                                        </Box>
                                    </Box>
                                ))}
                            </Fragment>
                        ))}
                    </Box>
                </Box>
            )}
            {withoutDates > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginTop: 1.5 }}>
                    {withoutDates} project(s) without an end date are not shown.
                </Typography>
            )}
        </Card>
    );
}

export default ProjectTimeline;
